import { Link } from "react-router-dom";
import { ArrowRight, Compass, Users, ListOrdered, Sparkles, BookOpen, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";

const modules = [
  {
    icon: Compass,
    title: "Your author positioning",
    body:
      "Before the first chapter, we establish what you are the authority on. Your career, your clients and the arguments you keep making become the spine of the book.",
  },
  {
    icon: Users,
    title: "The reader you are writing for",
    body:
      "A book for everyone persuades no one. We name the specific person, the moment they are in, and the outcome they want by the final page.",
  },
  {
    icon: Target,
    title: "The promise the book makes",
    body:
      "One clear promise, stated early and kept throughout. It shapes every chapter and it is what makes readers recommend the book to someone else.",
  },
  {
    icon: ListOrdered,
    title: "A chapter-by-chapter outline",
    body:
      "You leave with a structured outline: what each chapter argues, what evidence it uses, and how it moves the reader toward the promise.",
  },
  {
    icon: BookOpen,
    title: "A writing plan you can keep",
    body:
      "Word counts, sequence and milestones mapped to your actual calendar, so the manuscript progresses between client work rather than instead of it.",
  },
  {
    icon: Sparkles,
    title: "Readiness score",
    body:
      "An honest assessment of how publish-ready your concept is, and precisely which gaps to close before you start drafting.",
  },
];

const WriteYourBook = () => {
  return (
    <div className="min-h-screen">
      <Navbar />

      <PageHero
        eyebrow="Stage 01 · For new and aspiring authors"
        title={
          <>
            Write a book that sells{" "}
            <span className="text-gold-gradient">you</span> — not just an idea.
          </>
        }
        intro="You have the expertise and the message. Our interactive platform takes you from idea to a publish-ready concept, outline and writing plan — at your own pace, starting today. Expert human guidance is there when you want to escalate, but you never have to wait for a call to begin."
      >
        <Button asChild size="lg" className="bg-gold text-gold-foreground hover:bg-gold-light font-semibold px-8">
          <Link to="/sign-in" className="inline-flex items-center gap-2">
            Start Your Book Today
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
        <Button asChild size="lg" variant="outline" className="px-8">
          <Link to="/first-draft">Already have a draft?</Link>
        </Button>
      </PageHero>

      {/* The honest problem */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="grid gap-14 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="rule-gold" aria-hidden />
                <p className="eyebrow">Why most first books stall</p>
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-[2.75rem] font-semibold leading-[1.1] mb-6">
                The blank page is rarely the real problem.
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Aspiring authors do not stall because they cannot write. They stall because the book
                has no fixed shape — the reader keeps changing, the argument keeps widening, and every
                writing session starts from scratch. Fix the concept first and the drafting becomes
                mechanical.
              </p>
            </div>

            <ol className="space-y-6">
              {[
                "Is my idea actually strong enough for a whole book?",
                "Who is this for, specifically, and what do they get from it?",
                "What goes in which chapter — and in what order?",
                "How do I write this alongside a full client load?",
              ].map((q, i) => (
                <li key={q} className="flex items-baseline gap-6 border-b border-border pb-6">
                  <span className="font-serif italic text-gold text-sm tabular-nums">0{i + 1}</span>
                  <p className="font-serif text-xl md:text-2xl leading-snug">{q}</p>
                </li>
              ))}
              <li className="font-serif italic text-muted-foreground pt-2">
                We answer these before you write chapter one.
              </li>
            </ol>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="py-20 md:py-28 bg-card border-y border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mb-14">
            <div className="flex items-center gap-3 mb-5">
              <span className="rule-gold" aria-hidden />
              <p className="eyebrow">What you walk away with</p>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold leading-[1.1]">
              A concept, an outline, and a plan you will actually follow.
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {modules.map(({ icon: Icon, title, body }) => (
              <article key={title} className="card-noir-hover p-7">
                <span className="mb-6 flex h-11 w-11 items-center justify-center rounded-sm border border-border bg-background text-gold">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="text-xl font-semibold mb-3">{title}</h3>
                <p className="text-[15px] text-muted-foreground leading-relaxed">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mb-14">
            <div className="flex items-center gap-3 mb-5">
              <span className="rule-gold" aria-hidden />
              <p className="eyebrow">How it works</p>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold leading-[1.1]">
              From career story to writing roadmap.
            </h2>
          </div>

          <div className="grid gap-px bg-border md:grid-cols-3 overflow-hidden rounded-lg border border-border">
            {[
              {
                step: "01",
                title: "Share your background",
                desc: "Your professional history, the clients you serve, and the ideas you return to again and again.",
              },
              {
                step: "02",
                title: "Work the framework",
                desc: "We move module by module through positioning, reader, promise and structure — with guidance tailored to your material.",
              },
              {
                step: "03",
                title: "Export your plan",
                desc: "Your concept, outline and writing schedule in one document. Then start drafting — or hand the draft to us for review.",
              },
            ].map((s) => (
              <div key={s.step} className="bg-background p-8 md:p-10">
                <span className="font-serif italic text-gold text-lg">{s.step}</span>
                <h3 className="text-xl font-semibold mt-4 mb-3">{s.title}</h3>
                <p className="text-[15px] text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 card-noir p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between">
            <p className="font-serif text-xl md:text-2xl leading-snug max-w-2xl">
              Begin on the platform now — escalate to expert editorial guidance whenever you need it.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <Button asChild size="lg" className="bg-gold text-gold-foreground hover:bg-gold-light font-semibold">
                <Link to="/sign-in" className="inline-flex items-center gap-2">
            Start Your Book Today
            <ArrowRight className="h-4 w-4" />
          </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/first-draft" className="inline-flex items-center gap-2">
                  Go to Manuscript Review
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
      <Footer />
    </div>
  );
};

export default WriteYourBook;
